import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import DstButton from "../../components/DstButton/DstButton";
import { DstLiveTable } from "../../components/DstTable/DstLiveTable";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import AddDemoRecordPopup from "./Components/AddDemoRecordPopup/AddDemoRecordPopup";
import DemoRecordPopup from "./Components/DemoRecordPopup/DemoRecordPopup";
import { fetchAuthors, generateRemoteUrl } from "./DemoRecords.function";

const DemoRecords = () => {
  const { language, user_id } = useSession();
  const [selectedAuthors, setSelectedAuthors] = useState<(string | number)[]>([]);
  const [authors, setAuthors] = useState<Array<any>>([]);
  const [loadingAuthors, setLoadingAuthors] = useState(true);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<any | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const columns = [
    {
      name: translate(language, "GLOBAL.ID"),
      key: "id",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.NAME"),
      key: "name",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.AUTHOR"),
      key: "author.full_name",
      sortable: true,
      render: (item: any) => item.author?.full_name || "N/A",
    },
    {
      name: translate(language, "GLOBAL.DESCRIPTION"),
      key: "description",
      render: (item: any) => (
        <span>
          {item.description ? (
            item.description.length > 100 ? (
              `${item.description.substring(0, 100)}...`
            ) : (
              item.description
            )
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </span>
      ),
    },
    {
      name: translate(language, "PAGES.DEV.DEMO_RECORDS.LINKS"),
      key: "links",
      render: (item: any) => (
        <div className="d-flex gap-1">
          {item.documents_link && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="documents-tooltip">{translate(language, "PAGES.DEV.DEMO_RECORDS.DOCUMENTS_LINK")}</Tooltip>
              }
            >
              <a
                href={item.documents_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-secondary"
              >
                <span className="dst-icon-file-06" />
              </a>
            </OverlayTrigger>
          )}
          {item.record_link && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="record-tooltip">{translate(language, "PAGES.DEV.DEMO_RECORDS.RECORD_LINK")}</Tooltip>
              }
            >
              <a href={item.record_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">
                <span className="dst-icon-video-recorder" />
              </a>
            </OverlayTrigger>
          )}
          {item.slide_link && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="slide-tooltip">{translate(language, "PAGES.DEV.DEMO_RECORDS.SLIDE_LINK")}</Tooltip>}
            >
              <a href={item.slide_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">
                <span className="dst-icon-presentation-chart-01" />
              </a>
            </OverlayTrigger>
          )}
          {!(item.documents_link || item.record_link || item.slide_link) && <span className="text-muted">N/A</span>}
        </div>
      ),
    },
    {
      name: translate(language, "GLOBAL.CREATED_DATE"),
      key: "dt_created",
      sortable: true,
      render: (item: any) => <span>{formatDateHumanly(language, item.dt_created)}</span>,
    },
    {
      name: translate(language, "GLOBAL.UPDATED_DATE"),
      key: "dt_updated",
      sortable: true,
      render: (item: any) =>
        item?.dt_updated ? <span>{formatDateHumanly(language, item.dt_updated)}</span> : <span>-</span>,
    },
  ];

  useEffect(() => {
    fetchAuthors(language, setAuthors, setLoadingAuthors);
  }, [language]);

  return (
    <div className="px-3">
      <div className="d-flex justify-content-between py-3 flex-wrap">
        <h1 className="me-2">{translate(language, "PAGES.DEV.DEMO_RECORDS.TITLE")}</h1>
        <div className="d-flex align-items-center gap-2">
          <MultiFilterDropdown
            tags={authors.map((author) => ({
              display: author.full_name,
              value: author.user,
            }))}
            onSelectTags={(selectedIds) => setSelectedAuthors(selectedIds)}
            language={language}
            inputTitle={translate(language, "PAGES.DEV.DEMO_RECORDS.FILTER.AUTHORS")}
            inputLabel={translate(language, "PAGES.DEV.DEMO_RECORDS.FILTER.SELECT_AUTHORS")}
            inputPlaceholder={translate(language, "PAGES.DEV.DEMO_RECORDS.FILTER.SEARCH_AUTHORS")}
            showSearchBar
            mock={loadingAuthors}
          />
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "demos/records" }]}>
            <DstButton
              variant="primary"
              value={translate(language, "PAGES.DEV.DEMO_RECORDS.BUTTON_ADD")}
              clickFunction={() => setShowAddModal(true)}
              btnImageBefore={<span className="dst-icon-music-note-plus me-1" />}
            />
          </PermissionComponent>
        </div>
      </div>
      <DstLiveTable
        classTable="text-nowrap"
        remote={generateRemoteUrl(selectedAuthors, reloadKey)}
        remoteKey="demo_records_list"
        columns={columns}
        pagination
        paginationAlwaysShowFooter
        paginationResizeOptions={[20, 50, 100]}
        itemsPerPage={20}
        striped
        onRowClick={(_event, item) => {
          setSelectedDemo(item);
          setShowDemoModal(true);
        }}
      />
      <DemoRecordPopup
        show={showDemoModal}
        onClose={() => setShowDemoModal(false)}
        demo={selectedDemo}
        language={language}
      />
      <AddDemoRecordPopup
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        language={language}
        author_id={user_id!}
        onSuccess={() => setReloadKey((prev) => prev + 1)}
      />
    </div>
  );
};

export default DemoRecords;
